const STORAGE_KEY = 'jncquoi.shop-checkout';

export interface ShopCheckoutSession {
  /** The cart this payment was started for, so a session left over from another basket is ignored. */
  cartGuid: string;
  /** Set once an intent exists. Absent while the customer is still choosing a method. */
  paymentId?: string;
}

/**
 * Breadcrumb for a shop payment that outlives the page.
 *
 * The shop needs far less of this than the event and Mews flows do: its purchase is the cart, which
 * is server-side, so a refresh on the payment screen recovers everything from `GET /me/cart/shop`
 * and nothing has to be carried in router state.
 *
 * There is exactly one case it cannot recover from on its own, and it is the reason this file
 * exists. MB WAY confirms with `return_url` and comes back through a full page load; if the customer
 * approved, the webhook has already turned the cart *into* the order, so the cart is gone, the guid
 * is gone, and `payments/active` no longer lists the payment either — a settled payment is not
 * active. The screen would find nothing to pay for and bounce to the home page, having just charged
 * the customer. Remembering the payment id before confirming is what lets it read
 * `GET /me/checkout/payments/{id}` on the way back and go to the confirmation instead.
 *
 * Its own key, not the `jncquoi.checkout` one the event/Mews adapters share: those two discriminate
 * on a `kind` tag and only one of them can be mid-flight at a time, which is true of each other but
 * not of a shop basket sitting alongside a booking.
 *
 * `sessionStorage`, so it dies with the tab. Nothing sensitive: two ids the server re-authorises on
 * every read, and no amount — the price is never taken from here.
 */
export function rememberShopCheckout(session: ShopCheckoutSession): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    // Private browsing and storage quotas can both refuse writes. Everything except the MB WAY
    // redirect return works without this, so it must not break the screen.
    console.warn('rememberShopCheckout: could not persist checkout session:', error);
  }
}

export function recallShopCheckout(): ShopCheckoutSession | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<ShopCheckoutSession>;
    // A partial write is treated as absent — without a cart guid there is nothing to match against.
    return parsed?.cartGuid ? { cartGuid: parsed.cartGuid, paymentId: parsed.paymentId } : undefined;
  } catch (error) {
    console.warn('recallShopCheckout: could not read checkout session:', error);
    return undefined;
  }
}

export function forgetShopCheckout(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — a stale entry is overwritten by the next checkout and validated on recall.
  }
}
